import Core           from '@/core/Core';
import Dictionary     from '@/core/types/Dictionary';
import Vehicle        from '@/domains/Vehicles/Vehicle';
import VehiclesDAO    from '@/domains/Vehicles/VehiclesDAO';
import VehicleOwnershipType from '@/domains/Vehicles/VehicleOwnershipType';
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';
import ProductsDAO from "@/domains/Products/ProductsDAO";
import Product from "@/domains/Products/Product";
import VehicleType from "@/domains/VehicleTypes/VehicleType";

/**
 * Сервис для работы с транспортами
 */
class VehiclesService {

  /** Справочник форм владения транспортным средством. Заполняется в Dictionaries из getCombinedFrontendData */
  static vehicleOwnershipTypes: Dictionary<number, VehicleOwnershipType> = Dictionary.fromArray([]);

  /**
   * Загрузить один транспорт по id
   */
  static async get(id: number): Promise<Vehicle | null> {
    const result = (await this.getListPaginated({ id })).data;
    return result[0] ?? null;
  }

  /**
   * Загрузить список транспортов с пагинацией
   */
  static async getListPaginated(
    params?: Parameters<typeof VehiclesDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Vehicle[]; pagination: RpcDataPagination | null }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await VehiclesDAO.getList(filters, pagination, sort);

    return {
      data: Core.utils.hydrateArray(response.data, Vehicle.fromServer),
      pagination: response.meta?.pagination ?? null,
    };
  }

  /**
   * Загрузить список транспортов
   */
  static async getList(params?: Parameters<typeof VehiclesDAO.getList>[0]): Promise<Vehicle[]> {
    const response = await VehiclesDAO.getList(params);
    return Core.utils.hydrateArray(response.data, Vehicle.fromServer);
  }

  /**
   * Создать транспорт
   */
  static async add(vehicle: Vehicle): Promise<boolean> {
    const response = await VehiclesDAO.add(vehicle);

    if (response.ok) {
      Core.showNotification.success('Транспорт успешно добавлен');
    }

    return response.ok;
  }

  /**
   * Обновить транспорт
   */
  static async update(vehicle: Vehicle): Promise<boolean> {
    const response = await VehiclesDAO.update(vehicle);

    if (response.ok) {
      Core.showNotification.success('Транспорт успешно обновлён');
    }

    return response.ok;
  }

  /**
   * Удалить транспорт
   */
  static async delete(id: number): Promise<boolean> {
    const response = await VehiclesDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Транспорт успешно удалён');
    }

    return response.ok;
  }

  /**
   * Удалить транспорт с подтверждением через диалог
   */
  static async deleteWithConfirmation(vehicle: Vehicle): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить этот транспорт?',
      `Транспорт: "${vehicle.name}"`,
    );

    if (confirmed === true) {
      return await this.delete(vehicle.id);
    } else {
      return false;
    }
  }


  /**
   * Пункты контекстного меню транспорта для грида реестра и карточки.
   * Грид обновляет список, а карточка закрывается через resolve(), только если
   * callback пункта вернул true.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (vehicle: Vehicle) => { return this.showVehicleCard(vehicle); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'История изменений',
        'history',
        (vehicle: Vehicle) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'vehicle', entityId: vehicle.id, showAddComment: false }); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (vehicle: Vehicle) => { return this.deleteWithConfirmation(vehicle); },
      ),
    ];
  }

  /**
   * Открыть карточку.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showVehicleCard(vehicle: Vehicle | null, vehicleType: VehicleType | null = null): Promise<boolean> {
    return Core.dialogs.VehicleCard.showAsPromise({ vehicle: vehicle ?? null, defaultVehicleType: vehicleType ?? null });
  }
}

export default VehiclesService;